import { useIsAccept } from '@/features/auth';
import { LangEntity, UserRole } from '@/types';
import { dropHover } from '@/utils';
import { DragIndicator, MoreVert } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, TableCell, TableRow } from '@mui/material';
import { FC, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';

interface DndHintListItemProps {
  hintText: LangEntity;
  index: number;
  moveHint: (dragIndex: number, hoverIndex: number) => void;
  onRemove: () => void;
  onEdit: () => void;
}

interface DragItem {
  id: string;
  index: number;
}

export const DndHintListItem: FC<DndHintListItemProps> = ({ hintText, index, moveHint, onRemove, onEdit }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isRemoveAccept = useIsAccept(UserRole.ADMIN);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const ref = useRef<HTMLTableRowElement>(null);
  const [_, drop] = useDrop<DragItem, void>({
    accept: 'hint',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover: dropHover(ref, index, moveHint),
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'hint',
    item: () => {
      return { id: hintText, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handleMoreToggle = (): void => {
    setIsOpen((data) => !data);
  };

  const handleClose = (): void => {
    setIsOpen(false);
  };

  const handleRemove = (): void => {
    handleClose();
    onRemove();
  };

  drop(preview(ref));

  return (
    <TableRow ref={ref} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell ref={drag}>
        <IconButton sx={{ cursor: 'move' }}>
          <DragIndicator />
        </IconButton>
      </TableCell>
      <TableCell>
        {hintText.ru} ({hintText.en})
      </TableCell>
      <TableCell align="right">
        <div>
          <IconButton
            ref={anchorRef}
            aria-label="more"
            id="long-button"
            aria-controls={isOpen ? 'long-menu' : undefined}
            aria-expanded={isOpen ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleMoreToggle}
          >
            <MoreVert />
          </IconButton>
          <Menu
            id="long-menu"
            MenuListProps={{
              'aria-labelledby': 'long-button',
            }}
            anchorEl={anchorRef.current}
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
              style: {
                maxHeight: 48 * 4.5,
                width: '20ch',
              },
            }}
          >
            {isRemoveAccept ? <MenuItem onClick={handleRemove}>Удалить</MenuItem> : null}
            <MenuItem onClick={onEdit}>Редактировать</MenuItem>
            <MenuItem onClick={() => moveHint(index, 0)}>В самый верх</MenuItem>
            <MenuItem onClick={() => moveHint(index, -1)}>В самый низ</MenuItem>
          </Menu>
        </div>
      </TableCell>
    </TableRow>
  );
};
